import { AnimatePresence, motion, useAnimate } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../shared/components/Button';
import { ButtonsGroup } from '../../shared/components/ButtonsGroup';
import { OllieChatAvatar } from '../../shared/components/OllieChatAvatar';
import { SpeechBubble } from '../../shared/components/SpeechBubble';
import { useLoggedInUserEdit } from '../../shared/hooks/useLoggedInUserEdit';
import { ROUTES } from '../../shared/routing/routes';
import { OnboardingProgress } from '../../types/obsessless-user';
import { OcdCycle } from './components/OcdCycle';
import { PersonalizingProgressDisplay } from './components/PersonalizingProgressDisplay';
import { useOnboardingOcdCycleStore } from './hooks/useOnboardingOcdCycleStore';
import { useOnboardingStore } from './hooks/useOnboardingStore';

const progressMessagesOcdCycle = [
    "Creating quests...",
    "Preparing lessons...",
    "Creating SOS tools...",
    "Your OCD Plan is almost ready....",
    "Your OCD Plan is ready.",
];

const listItemsOcdCycle = [
    { text: "Cut down compulsions step by step", isVisible: false },
    { text: "Face intrusive thoughts with support", isVisible: false },
    { text: "Break the reassurance cycle", isVisible: false },
    { text: "Handle OCD flare-ups more confidently", isVisible: false }
];

export const OcdCycleExplain = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const loadOcdCycleSteps = useOnboardingOcdCycleStore((state) => state.loadOcdCycleSteps);
    const currentOcdCycleStep = useOnboardingOcdCycleStore((state) => state.currentOcdCycleStep);
    const isZoomedIn = useOnboardingOcdCycleStore((state) => state.isZoomedIn);
    const setStep = useOnboardingOcdCycleStore((state) => state.setStep);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [scope, animate] = useAnimate();
    const setCurrentStep = useOnboardingStore(state => state.setCurrentStep);
    const [zoomedInTitle, setZoomedInTitle] = useState('Creating your personalised OCD plan...');
    const [zoomedInProgressTitle, setZoomedInProgressTitle] = useState('Building your 14-day plan to help you...');
    const { saveOnboardingProgress } = useLoggedInUserEdit();

    const cycleStep = parseInt(searchParams.get('step') || '0', 10);

    useEffect(() => {
        loadOcdCycleSteps();
    }, [loadOcdCycleSteps]);

    const onboardingStep = 5;

    useEffect(() => {
        if (isZoomedIn) {
            setCurrentStep(undefined);
            return;
        }
        setCurrentStep(onboardingStep + cycleStep);
        setStep(cycleStep);
    }, [setCurrentStep, isZoomedIn, cycleStep]);

    useEffect(() => {
        const handleZoomInAnimation = async () => {
            await animate("#ocdCycleContainer", {
                scale: 2,
                translateY: 60,
            }, {
                duration: 0.8,
                ease: "easeInOut"
            });
        }
        if (isZoomedIn) {
            handleZoomInAnimation();
        }
    }, [isZoomedIn, animate]);

    const handleSpeechBubbleComplete = () => {
        setIsButtonDisabled(false);
    };

    const handleStepForward = async () => {
        if (isZoomedIn) {
            await saveOnboardingProgress(OnboardingProgress.FinishedOnboarding);
            navigate(ROUTES.onboarding.paywall);
            return;
        }

        setIsButtonDisabled(true);

        const newCycleStep = cycleStep + 1;
        setSearchParams({ step: newCycleStep.toString() });
        setStep(newCycleStep);
    };

    const handleAnimationComplete = async () => {
        setIsButtonDisabled(false);
        setZoomedInTitle('All Set, personalised OCD plan is ready!');
        setZoomedInProgressTitle('Your OCD Plan is Ready..');
    }

    return (
        <div ref={scope} className='relative flex flex-col gap-4 safe-pb-4 flex-1 overflow-hidden'>
            <title>Ocd Cycle Explain</title>
            {isZoomedIn && (
                <AnimatePresence>
                    <motion.h1
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1 }}
                        className='text-2xl font-semibold flex-shrink-0 px-8'>{zoomedInTitle}</motion.h1>
                </AnimatePresence>
            )}
            <div id="ocdCycleContainerWrapper" className={`flex-1 flex flex-col justify-center items-center overflow-hidden`}>
                <div id="ocdCycleContainer" className={`overflow-hidden ${isZoomedIn ? 'h-[75px] p-0' : 'h-auto p-6'}`}>
                    <OcdCycle currentStep={cycleStep}>
                        {!isZoomedIn && <div className='flex flex-col items-center justify-center gap-4'>
                            <SpeechBubble text={currentOcdCycleStep.ollieText} size='small' className='!max-w-[100%]' animationDelay={0.2} inAnimationDuration={0.3} useTypingEffect={false} onTypingComplete={handleSpeechBubbleComplete} />
                            <OllieChatAvatar />
                        </div>
                        }
                    </OcdCycle>
                </div>
            </div>
            {isZoomedIn && (
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1, delay: 0.8 }}
                        className='flex flex-col justify-center items-center overflow-hidden px-8'
                    >
                        <PersonalizingProgressDisplay
                            title={zoomedInProgressTitle}
                            progressMessages={progressMessagesOcdCycle}
                            listItems={listItemsOcdCycle}
                            hideWhenComplete={false}
                            onAnimationComplete={handleAnimationComplete} />
                    </motion.div>
                </AnimatePresence>
            )}
            <div className="flex flex-col items-center gap-4 flex-shrink-0 px-8 z-10">
                <ButtonsGroup>
                    <Button
                        inactive={isButtonDisabled}
                        onClick={handleStepForward}
                        label={isZoomedIn ? "Unlock my plan now!" : currentOcdCycleStep.buttonLabel}
                    />
                </ButtonsGroup>
            </div>
        </div>
    );
};